import React from 'react'
// @ts-expect-error
import { Fancybox } from '@fancyapps/ui'
import '@fancyapps/ui/dist/fancybox.css'

import * as c from '../../../common'

import PageReport from '../../comps/PageReport'

import styles from './MediaGridReport.module.css'

// Jaron (Slack, 2026-08): "sections with the title and pictures that are
// smaller so maybe 8 to 10 pictures per page and you scroll over each
// picture and it enlarges... each picture would have notes underneath it".
// 9 fits a clean 3x3 grid within that range.
const PICTURES_PER_PAGE = 9

export interface MediaGridReportProps {
  company: string
  items: readonly c.MediaData[]
  /** Section divider shown once, above the first page, only when photos exist. */
  kicker?: string
  title: string
}

export default React.memo<MediaGridReportProps>(function MediaGridReport({
  company,
  items,
  kicker,
  title,
}) {
  const groupKey = `media-grid-${title}`

  const pages = React.useMemo((): (readonly c.MediaData[])[] => {
    const usable = c.renderableMedia(items)
    const result: c.MediaData[][] = []
    for (let i = 0; i < usable.length; i += PICTURES_PER_PAGE) {
      result.push(usable.slice(i, i + PICTURES_PER_PAGE))
    }
    return result
  }, [items])

  React.useEffect(() => {
    Fancybox.bind(`[data-fancybox="${groupKey}"]`, {
      Thumbs: {
        autoStart: false,
      },
    })
  }, [groupKey])

  // Was `items.length === 0`. A section holding nothing BUT sourceless records
  // would otherwise render its title and company border over an empty grid.
  if (pages.length === 0) return null

  return (
    <>
      {pages.map((pageItems, pageIndex) => (
        // eslint-disable-next-line react/no-array-index-key
        <PageReport
          company={company}
          companyBorder
          beforeTitle={
            pageIndex === 0 && kicker ? (
              <div className={styles['kicker']}>{kicker}</div>
            ) : undefined
          }
          title={title}
          key={pageIndex}
        >
          <div className={styles['grid']}>
            {pageItems.map((item) => (
              <div className={styles['cell']} key={item.url}>
                <div className={styles['frame']}>
                  {item.type === 'img' ? (
                    <img
                      alt="Customer Images"
                      className={styles['image']}
                      // Lets the PDF export draw this image to a canvas without
                      // tainting it (B137 — tainted canvas made toDataURL throw
                      // in downloadReportAsPdf.ts, so the photo pages came out
                      // as broken-image glyphs). Requires the storage bucket's
                      // CORS policy to allow this origin: prod (www.
                      // ecohomereports.com) is on the allowlist; localhost is
                      // NOT, so on localhost these images will fail to load
                      // entirely — expected, not a regression.
                      crossOrigin="anonymous"
                      data-fancybox={groupKey}
                      data-src={item.url}
                      src={item.url}
                    />
                  ) : (
                    <video
                      className={styles['video']}
                      controls
                      src={item.url}
                    />
                  )}
                </div>

                {item.description && (
                  <span className={styles['description']}>
                    {item.description}
                  </span>
                )}
              </div>
            ))}
          </div>
        </PageReport>
      ))}
    </>
  )
})
